// 登录状态管理工具

const AUTH_STORAGE_KEYS = ['token', 'isLoggedIn', 'userPhone', 'userInfo', 'role', 'nickname', 'uniqueId', 'adminId', 'desktopLastRoute']
const DEFAULT_DESKTOP_ROUTE = '/dashboard/document-list'

const getDesktopApi = () => (typeof window !== 'undefined' ? window.ibotDesktop : null)

const decodeJwtPayload = (token) => {
  try {
    const parts = String(token || '').split('.')
    if (parts.length < 2) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=')
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

export const isTokenExpired = (token) => {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return false
  return Date.now() >= payload.exp * 1000
}

let persistTimer = null

const persistDesktopAuth = ({ immediate = false } = {}) => {
  const desktop = getDesktopApi()
  if (!desktop?.saveAuth) return

  const run = () => {
    persistTimer = null
    const data = {}
    AUTH_STORAGE_KEYS.forEach((key) => {
      const value = localStorage.getItem(key)
      if (value != null) data[key] = value
    })
    if (data.token) {
      void desktop.saveAuth(data)
    } else {
      void desktop.clearAuth?.()
    }
  }

  if (immediate) {
    if (persistTimer) {
      clearTimeout(persistTimer)
      persistTimer = null
    }
    run()
    return
  }

  if (persistTimer) clearTimeout(persistTimer)
  persistTimer = setTimeout(run, 400)
}

export const restoreDesktopAuth = async () => {
  const desktop = getDesktopApi()
  if (!desktop?.loadAuth) return
  const data = await desktop.loadAuth()
  if (!data?.token) return
  if (isTokenExpired(data.token)) {
    await desktop.clearAuth?.()
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
    return
  }
  AUTH_STORAGE_KEYS.forEach((key) => {
    if (typeof data[key] === 'string') {
      localStorage.setItem(key, data[key])
    }
  })
  localStorage.setItem('isLoggedIn', 'true')
}

export const getDesktopHomeRoute = () => {
  const path = localStorage.getItem('desktopLastRoute')
  return path && path.startsWith('/dashboard') ? path : DEFAULT_DESKTOP_ROUTE
}

export const rememberDesktopRoute = (path) => {
  if (!path || !path.startsWith('/dashboard')) return
  localStorage.setItem('desktopLastRoute', path)
  persistDesktopAuth()
}

export const setToken = (token) => {
  localStorage.setItem('token', token)
  persistDesktopAuth({ immediate: true })
}

export const getToken = () => {
  return localStorage.getItem('token')
}

export const removeToken = () => {
  localStorage.removeItem('token')
  persistDesktopAuth({ immediate: true })
}

export const AUTH_UPDATED_EVENT = 'ibot-auth-updated'

const FILE_BASE = 'https://www.ibot.top/file'

export const notifyAuthUpdated = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(AUTH_UPDATED_EVENT))
}

export const normalizeAvatarUrl = (value, uniqueId) => {
  const text = String(value || '').trim()
  if (!text) return ''
  if (/^https?:\/\//i.test(text) || text.startsWith('data:') || text.startsWith('blob:')) {
    return text
  }
  if (text.includes('userAvatar')) {
    return `${FILE_BASE}/image/${text}`
  }
  if (uniqueId == null || uniqueId === '') return ''
  const name = /\.(jpe?g|png|gif|webp)$/i.test(text) ? text : `${text}.jpg`
  return `${FILE_BASE}/chat/${uniqueId}/image/avatar/${name}`
}

export const resolveAvatarUrl = (user) => {
  const uniqueId = user?.uniqueId ?? localStorage.getItem('uniqueId')
  const raw = user?.avatar || user?.headImg || user?.headUrl || user?.portrait || user?.photo || ''
  return normalizeAvatarUrl(raw, uniqueId)
}

export const setUserInfo = (user) => {
  if (!user) return
  localStorage.setItem('userInfo', JSON.stringify(user))
  if (user.nickname) {
    localStorage.setItem('nickname', user.nickname)
  }
  if (user.roles) {
    localStorage.setItem('role', user.roles)
  }
  if (user.uniqueId != null) {
    localStorage.setItem('uniqueId', String(user.uniqueId))
  }
  persistDesktopAuth({ immediate: true })
}

export const getUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || 'null')
  } catch {
    return null
  }
}

export const setLoginStatus = (isLoggedIn, userPhone = '') => {
  localStorage.setItem('isLoggedIn', isLoggedIn.toString())
  if (userPhone) {
    localStorage.setItem('userPhone', userPhone)
  }
  persistDesktopAuth({ immediate: true })
}

export const getLoginStatus = () => {
  return localStorage.getItem('isLoggedIn') === 'true'
}

export const getUserPhone = () => {
  return localStorage.getItem('userPhone')
}

export const clearLoginStatus = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
  getDesktopApi()?.clearAuth?.()
}

export const isAuthenticated = () => {
  const token = getToken()
  if (!token) return false
  if (isTokenExpired(token)) {
    clearLoginStatus()
    return false
  }
  if (localStorage.getItem('isLoggedIn') !== 'true') {
    localStorage.setItem('isLoggedIn', 'true')
  }
  return true
}
